import { BaseInput } from "shared/components/forms/BaseInput";
import { tenDigitTelParser } from "shared/components/forms/BaseInput/parsers";
import { ColorPicker } from "./components/ColorPicker";
import { LogoDropZone } from "./components/LogoDropZone";
import { getPropertyPresigned } from "../AddProperty/untils";
import { generatePrimaryPalette } from "./components/ColorPicker/untils";
import { uploadFileToS3Bucket, trimImageWhitespace } from "utils/fileHandlers";
import { SectionCard } from "shared/app/SectionCard";
import { RegisterTyped } from "shared/hooks/use-form-typed";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { checkImageQuality } from "./components/LogoDropZone/utils";

interface BrandingFieldsProps {
  register: RegisterTyped<{
    enabled?: boolean;
    logo?: string;
    primary_color?: string;
    landlord_name?: string;
    landlord_phone?: string;
    landlord_email?: string;
    palette?: Record<string, string>;
  }>;
  sessionKey: string;
  showLandlordFields?: boolean;
  onUploadingChange?: (isUploading: boolean) => void;
}

export const BrandingFields = ({
  register,
  sessionKey,
  showLandlordFields = false,
  onUploadingChange
}: BrandingFieldsProps) => {
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(
    register("logo").value
  );

  const { mutate: handleLogoUpload, isPending } = useMutation({
    mutationFn: async (file: File) => {
      const quality = await checkImageQuality(file);
      if (!quality.ok) toast.warn(quality.reason);

      const trimmedFile = await trimImageWhitespace(file);
      const presigned = await getPropertyPresigned(trimmedFile, sessionKey);
      await uploadFileToS3Bucket(presigned.response.url, trimmedFile);
      return {
        globalId: presigned.asset.global_id as string,
        file: trimmedFile
      };
    },
    onMutate: () => onUploadingChange?.(true),
    onSuccess: ({ globalId, file }) => {
      register("logo").onChange(globalId);
      const reader = new FileReader();
      reader.onload = () => setPreviewUrl(reader.result as string);
      reader.readAsDataURL(file);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
    onSettled: () => onUploadingChange?.(false)
  });

  const handleLogoSelect = (file: File | null) => {
    if (!file) {
      register("logo").onChange(undefined);
      setPreviewUrl(undefined);
      return;
    }
    handleLogoUpload(file);
  };

  const handleColorChange = (color: string) => {
    const palette = generatePrimaryPalette(color);
    register("primary_color").onChange(palette[500]);
    register("palette").onChange(palette);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-6 items-center">
        <LogoDropZone
          onFileSelect={handleLogoSelect}
          logoUrl={previewUrl}
          isProcessing={isPending}
          processingLabel={isPending ? "Uploading..." : undefined}
        />
        <div>
          <ColorPicker
            value={register("primary_color").value || ""}
            onChange={handleColorChange}
            label="Primary color"
            placeholder="#E6007E"
            defaultValue="#E6007E"
            error={register("primary_color").error}
          />
        </div>
      </div>

      {showLandlordFields && (
        <div className="grid grid-cols-2 gap-6">
          <BaseInput
            id="landlord-name"
            label="Landlord name"
            placeholder="John Doe"
            maxLength={100}
            {...register("landlord_name")}
          />
          <BaseInput
            id="landlord-phone"
            label="Landlord phone"
            placeholder="(555) 123-4567"
            parser={tenDigitTelParser({ asString: true })}
            value={register("landlord_phone").value ?? ""}
            onChange={register("landlord_phone").onChange}
            error={register("landlord_phone").error}
          />
          <BaseInput
            id="landlord-email"
            label="Landlord email"
            placeholder="landlord@example.com"
            type="email"
            {...register("landlord_email")}
          />
        </div>
      )}
    </div>
  );
};

interface BrandingFormProps {
  headerExtra?: React.ReactNode;
  footer?: React.ReactNode;
  register: RegisterTyped<{
    enabled?: boolean;
    logo?: string;
    primary_color?: string;
    landlord_name?: string;
    landlord_phone?: string;
    landlord_email?: string;
    palette?: Record<string, string>;
  }>;
  sessionKey: string;
  onUploadingChange?: (isUploading: boolean) => void;
  showLandlordFields?: boolean;
}

export const BrandingForm = ({
  register,
  sessionKey,
  headerExtra,
  footer,
  onUploadingChange,
  showLandlordFields = true
}: BrandingFormProps) => {
  return (
    <SectionCard title="Branding" headerExtra={headerExtra} footer={footer}>
      <BrandingFields
        register={register}
        sessionKey={sessionKey}
        showLandlordFields={showLandlordFields}
        onUploadingChange={onUploadingChange}
      />
    </SectionCard>
  );
};
